import type { QuickJSContext, QuickJSHandle, QuickJSWASMModule } from 'quickjs-emscripten-core';
import {
  DiceError,
  DiceExpression,
  ENTITY_TYPE_NAMES,
  GENERATED_ENTRIES_MAX,
  JavaRandom,
  choiceIndex,
  generateCity,
  generateFaction,
  generateHistory,
  generateLanguage,
  generateName,
  generateNpc,
  generateTavern,
  randInt,
  type GeneratedEntry,
} from '@realmery/realm-core';

/**
 * Runs a JavaScript script in QuickJS (docs/PARITY-STEP-2-PLAN.md section 6, WEB-PLAN.md
 * section 5): a separate JavaScript engine compiled to WebAssembly, so a script sees nothing of
 * the browser - no network, no page, no storage, no `fetch`, `postMessage` or `importScripts` -
 * only the functions handed to it here, the ones a desktop script has:
 *
 * - `generate_name()`, `generate_city()` ... `generate_language()`, `rand_choice(list)`,
 *   `rand_int(min, max)` and `roll("2d6 + 1")`, all drawing on one seeded random source, so
 *   a run can be repeated (decision 4);
 * - `log(...)` (or `print`) for the run's output;
 * - `create_city(...)` ... `create_language(...)` and `create_entry(type, title,
 *   description)`, which create nothing: they list the entries the run would make, for you to
 *   approve (decision 3).
 *
 * A script that defines `generate()` and creates nothing has it called, and its result shown,
 * as the desktop's generator editor's Test Run does. Memory, stack, time, output and entries
 * are all limited, and sandbox.worker.ts runs this in a Worker of its own, ended from outside if
 * it overruns.
 */

export interface RunRequest {
  readonly source: string;
  /** A seed to repeat a run with; a fresh one when null. */
  readonly seed: string | null;
}

export interface RunResult {
  readonly ok: boolean;
  /** The seed used, to repeat the run. */
  readonly seed: string;
  readonly log: string;
  /** What `create_*` would make. */
  readonly entries: GeneratedEntry[];
  /** What `generate()` returned, when the script defines one and created nothing. */
  readonly generated: Readonly<Record<string, string | number | boolean | string[]>> | null;
  readonly error: string | null;
}

export const LIMITS = {
  memoryBytes: 32 * 1024 * 1024,
  stackBytes: 512 * 1024,
  millis: 5_000,
  logChars: 64_000,
  entries: GENERATED_ENTRIES_MAX,
  titleChars: 200,
  textChars: 20_000,
  fields: 50,
} as const;

const SEED = /^-?\d{1,19}$/;

export function freshSeed(): string {
  const words = crypto.getRandomValues(new Uint32Array(2));
  return String((BigInt(words[0]! & 0xffff) << 32n) | BigInt(words[1]!));
}

/** Entry types a script may name, as the desktop's EntityType; anything else is a custom type of that name. */
const ENTITY_TYPES = new Set(Object.keys(ENTITY_TYPE_NAMES));

/** The `create_*` functions: the entry type each makes, and the label it adds, as the desktop's. */
const CREATORS: Readonly<Record<string, readonly [string, string | null]>> = {
  create_city: ['PLACE', null],
  create_npc: ['PERSON', null],
  create_faction: ['FACTION', null],
  create_tavern: ['PLACE', 'tavern'],
  create_history: ['EVENT', null],
  create_language: ['CONCEPT', 'language'],
};

class ScriptStop extends Error {}

export function runInQuickJS(QuickJS: QuickJSWASMModule, request: RunRequest, clock: () => number = () => Date.now()): RunResult {
  const seed = request.seed ?? freshSeed();
  const failed = (error: string): RunResult => ({ ok: false, seed, log: '', entries: [], generated: null, error });
  if (!SEED.test(seed)) return failed('A seed is a whole number, like 42.');
  const random = new JavaRandom(BigInt.asIntN(64, BigInt(seed)));

  let log = '';
  let logFull = false;
  const entries: GeneratedEntry[] = [];
  const deadline = clock() + LIMITS.millis;

  const runtime = QuickJS.newRuntime();
  runtime.setMemoryLimit(LIMITS.memoryBytes);
  runtime.setMaxStackSize(LIMITS.stackBytes);
  runtime.setInterruptHandler(() => clock() > deadline);
  const vm = runtime.newContext();
  try {
    const define = (name: string, fn: (...args: QuickJSHandle[]) => QuickJSHandle | void) => {
      const handle = vm.newFunction(name, fn);
      vm.setProp(vm.global, name, handle);
      handle.dispose();
    };

    const generators: Record<string, () => unknown> = {
      generate_name: () => generateName(random),
      generate_city: () => generateCity(random),
      generate_npc: () => generateNpc(random),
      generate_faction: () => generateFaction(random),
      generate_tavern: () => generateTavern(random),
      generate_history: () => generateHistory(random),
      generate_language: () => generateLanguage(random),
    };
    for (const [name, generate] of Object.entries(generators)) define(name, () => toHandle(vm, generate()));

    define('rand_choice', (list) => {
      const length = list ? vm.getProp(list, 'length') : undefined;
      const size = length && vm.typeof(length) === 'number' ? vm.getNumber(length) : NaN;
      length?.dispose();
      if (!list || vm.typeof(list) !== 'object' || !Number.isInteger(size) || size <= 0) throw new TypeError('rand_choice() needs a list to choose from, like rand_choice(["a", "b"])');
      return vm.getProp(list, choiceIndex(random, size));
    });
    define('rand_int', (low, high) => {
      const min = low && vm.typeof(low) === 'number' ? vm.getNumber(low) : NaN;
      const max = high && vm.typeof(high) === 'number' ? vm.getNumber(high) : NaN;
      if (!Number.isSafeInteger(min) || !Number.isSafeInteger(max)) throw new TypeError('rand_int() needs two whole numbers, like rand_int(1, 6)');
      return vm.newNumber(randInt(random, min, max));
    });
    define('roll', (expression) => {
      if (!expression || vm.typeof(expression) !== 'string') throw new TypeError('roll() needs a roll to make, like roll("2d6 + 1")');
      try {
        return vm.newNumber(Number(DiceExpression.parse(vm.getString(expression)).roll(random).total));
      } catch (e) {
        if (e instanceof DiceError) throw new Error(e.message);
        throw e;
      }
    });
    const write = (...args: QuickJSHandle[]) => {
      if (logFull) return;
      const line = `${args.map((arg) => shown(vm.dump(arg))).join(' ')}\n`;
      if (log.length + line.length > LIMITS.logChars) {
        log += `${line.slice(0, Math.max(0, LIMITS.logChars - log.length))}\n(The rest of the output was left out: a run shows at most ${LIMITS.logChars.toLocaleString('en')} characters.)\n`;
        logFull = true;
      } else log += line;
    };
    define('log', write);
    define('print', write);

    const add = (entry: GeneratedEntry) => {
      if (entries.length >= LIMITS.entries) throw new ScriptStop(`A run can make at most ${LIMITS.entries} entries, so it was stopped.`);
      entries.push(entry);
      return vm.newString(`new-entry-${entries.length}`);
    };
    for (const [name, [entityType, label]] of Object.entries(CREATORS)) {
      define(name, (data) => {
        const fields = data ? vm.dump(data) : undefined;
        if (!fields || typeof fields !== 'object') throw new TypeError(`expected an object, e.g. ${name}(generate_${name.slice(7)}())`);
        const record = fields as Record<string, unknown>;
        return add({ entityType, title: cut(text(record['title'], 'Untitled'), LIMITS.titleChars), description: cut(text(record['description'], ''), LIMITS.textChars), labels: label ? [label] : [], metadata: [] });
      });
    }
    define('create_entry', (type, title, description) => {
      if (!type || !title || vm.typeof(type) === 'undefined' || vm.typeof(title) === 'undefined') throw new TypeError("create_entry(type, title, description='') requires at least a type and title");
      const typeName = text(vm.dump(type), '');
      const upper = typeName.trim().toUpperCase();
      const known = ENTITY_TYPES.has(upper) && upper !== 'CUSTOM';
      const about = description && vm.typeof(description) !== 'undefined' ? vm.dump(description) : null;
      return add({
        entityType: known ? upper : 'CUSTOM',
        ...(known ? {} : { customTypeName: cut(typeName.trim() || 'Custom', 100) }),
        title: cut(text(vm.dump(title), 'Untitled'), LIMITS.titleChars),
        description: cut(text(about, ''), LIMITS.textChars),
        labels: [],
        metadata: [],
      });
    });

    const result = vm.evalCode(request.source, 'script.js');
    if (result.error) {
      const message = describe(vm.dump(result.error), clock() > deadline);
      result.error.dispose();
      return { ok: false, seed, log, entries: [], generated: null, error: message };
    }
    result.value.dispose();

    let generated: RunResult['generated'] = null;
    const generate = vm.getProp(vm.global, 'generate');
    if (vm.typeof(generate) === 'function' && entries.length === 0) {
      const call = vm.callFunction(generate, vm.undefined);
      if (call.error) {
        const message = describe(vm.dump(call.error), clock() > deadline);
        call.error.dispose();
        generate.dispose();
        return { ok: false, seed, log, entries: [], generated: null, error: message };
      }
      const value = vm.dump(call.value);
      call.value.dispose();
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        generate.dispose();
        return { ok: false, seed, log, entries: [], generated: null, error: 'generate() should return an object, like { title: "...", description: "..." }.' };
      }
      generated = plain(value as Record<string, unknown>);
    }
    generate.dispose();
    return { ok: true, seed, log, entries, generated, error: null };
  } catch (e) {
    // A host function's own stop (too many entries), or QuickJS running out of room.
    return { ok: false, seed, log, entries: [], generated: null, error: e instanceof ScriptStop ? e.message : describe(e, clock() > deadline) };
  } finally {
    // After a stack overflow QuickJS can fail its own clean-up check; the result is already
    // worked out, and the Worker is ended after one run anyway.
    try {
      vm.dispose();
      runtime.dispose();
    } catch {
      // Left to the Worker's end.
    }
  }
}

/** A script error in plain words, with its line when QuickJS says. */
function describe(error: unknown, timedOut: boolean): string {
  const e = (error && typeof error === 'object' ? error : { message: String(error) }) as { name?: string; message?: string; stack?: string };
  const message = e.message ?? String(error);
  if (timedOut || /interrupted/i.test(message)) return `The script ran for more than ${LIMITS.millis / 1000} seconds, so it was stopped.`;
  if (/out of memory/i.test(message)) return 'The script used too much memory, so it was stopped.';
  if (/stack overflow|call stack size exceeded/i.test(message)) return 'The script called itself too deeply (a loop of calls that never ends?), so it was stopped.';
  if (/at most \d+ entries/.test(message)) return message;
  const line = /script\.js:(\d+)/.exec(e.stack ?? '')?.[1];
  return `${e.name ?? 'Error'}: ${message}${line ? ` (line ${line})` : ''}`;
}

/** What `log` shows for a value: text as it is, anything else as JSON. */
function shown(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === undefined) return 'undefined';
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

/** As the desktop's ScriptValues.stringField: the fallback for nothing, the text of anything else. */
function text(value: unknown, fallback: string): string {
  if (value === undefined || value === null) return fallback;
  return typeof value === 'string' ? value : shown(value);
}

const cut = (value: string, max: number) => (value.length > max ? value.slice(0, max) : value);

/** A generate() result as plain fields: text, numbers, yes/no, and lists of text. */
function plain(value: Record<string, unknown>): Record<string, string | number | boolean | string[]> {
  const out: Record<string, string | number | boolean | string[]> = {};
  for (const [key, field] of Object.entries(value).slice(0, LIMITS.fields)) {
    if (typeof field === 'number' || typeof field === 'boolean') out[cut(key, 100)] = field;
    else if (Array.isArray(field)) out[cut(key, 100)] = field.slice(0, 100).map((item) => cut(text(item, ''), 1000));
    else if (field !== undefined && field !== null) out[cut(key, 100)] = cut(text(field, ''), LIMITS.textChars);
  }
  return out;
}

/** A plain value into the script's world. */
function toHandle(vm: QuickJSContext, value: unknown): QuickJSHandle {
  if (typeof value === 'string') return vm.newString(value);
  if (typeof value === 'number') return vm.newNumber(value);
  if (typeof value === 'boolean') return value ? vm.true : vm.false;
  if (Array.isArray(value)) {
    const array = vm.newArray();
    value.forEach((item, i) => {
      const handle = toHandle(vm, item);
      vm.setProp(array, i, handle);
      handle.dispose();
    });
    return array;
  }
  if (value && typeof value === 'object') {
    const object = vm.newObject();
    for (const [key, item] of Object.entries(value)) {
      const handle = toHandle(vm, item);
      vm.setProp(object, key, handle);
      handle.dispose();
    }
    return object;
  }
  return vm.undefined;
}
